<template>
  <header v-if="user" class="cc-ui-navigation">
    <nav class="cc-ui-navigation__main">
      <cognitive-list>
        <cognitive-list-item>
          <router-link class="cc-ui-navigation__brand" :to="'/'" aria-label="Cognitive Credit">
            <Logo class="cc-ui-navigation__logo" />
          </router-link>
        </cognitive-list-item>
        <template v-for="(list, index) in menu" :key="`menu-list-${index}`">
          <cognitive-list-item class="cc-ui-navigation__link">
            <template v-if="list.isExternal">
              <a :href="list.path" :data-test-handle="`cc-ui-navigation-link-${list.path}`">
                {{ list.text }}
              </a>
            </template>
            <template v-else-if="list.subMenu">
              <cognitive-dropdown
                close-on-leave
                overlap
                content-pos="left"
                :data-title="list.title"
                :class="{
                  'cc-ui-navigation__link': true,
                  'is-active': $route?.fullPath.includes(list.path),
                  'cc-ui-navigation__link--disabled': list.disabled,
                }"
                type="button"
                @click="dropdownClickHandler($event, list)">
                <template #buttonText> {{ list.text }}</template>
                <template #blockText>
                  <SubNav :items="list.subMenu" />
                </template>
              </cognitive-dropdown>
            </template>
            <template v-else>
              <router-link :to="list.path" :data-test-handle="`cc-ui-navigation-link-${list.path}`">
                {{ list.text }}
              </router-link>
            </template>
          </cognitive-list-item>
        </template>
        <cognitive-list-item>
          <BetaBanner />
        </cognitive-list-item>
      </cognitive-list>
      <cognitive-list class="cc-ui-navigation__tools">
        <cognitive-list-item>
          <cognitive-tag
            v-if="showTrialBanner"
            size="xs"
            class="cc-ui-navigation__banner-link"
            :bg-color="trialBannerColours.background"
            :text-color="trialBannerColours.text">
            {{ trialBannerText }}
          </cognitive-tag>
        </cognitive-list-item>
        <cognitive-list-item v-if="survey?.showButton">
          <cognitive-button
            version="link"
            class="cc-ui-navigation__survey-link"
            @click="openModal({ name: 'surveyModal' })">
            <cognitive-tag size="xs" :bg-color="cognitiveColours.goldenTanoi" :text-color="cognitiveColours.blackPearl">
              We'd like your feedback
            </cognitive-tag>
          </cognitive-button>
        </cognitive-list-item>
        <cognitive-list-item padding="0 sm 0 0" class="cc-ui-navigation__terminal">
          <button @click="toggleTerminal(true)">
            Terminal <span>({{ terminalShortcutString }})</span>
          </button>
        </cognitive-list-item>
        <template v-if="!isAdminPanel">
          <cognitive-list-item padding="0 sm 0 0">
            <cognitive-dropdown
              close-on-leave
              overlap
              content-pos="right"
              class="cc-ui-navigation__link cc-ui-navigation__link--help">
              <template #buttonText> Help </template>
              <template #blockText>
                <HelpDropdown />
              </template>
            </cognitive-dropdown>
          </cognitive-list-item>
        </template>
        <template v-if="companyWatcher">
          <cognitive-list-item padding="0 xs 0 0">
            <cognitive-dropdown
              overlap
              name="Notification centre"
              content-pos="right"
              class="cc-ui-navigation__bell cc-ui-navigation__link"
              :class="{
                'cc-ui-navigation__link--has-notifications': notifications.length > 0,
                'cc-ui-navigation__link--no-notifications': notifications.length <= 0,
              }"
              :data-title-right="notifications.length <= 0 ? 'You currently have no notifications' : null">
              <template #buttonText>
                <cognitive-icon icon-type="css" icon-name="IconNotification" />
              </template>
              <template #blockText>
                <NotificationMenu />
              </template>
            </cognitive-dropdown>
          </cognitive-list-item>
        </template>
        <template v-else>
          <cognitive-list-item padding="0 xs 0 0">
            <cognitive-button
              class="cc-ui-navigation__bell cc-ui-navigation__reconnect"
              version="link"
              data-title-right="Notifications offline, Click to reconnect"
              @click="listenCompanyChanges()">
              <cognitive-icon icon-type="css" icon-name="IconNotificationOffline" />
            </cognitive-button>
          </cognitive-list-item>
        </template>

        <template v-if="isAdmin || isDataAnalyst">
          <template v-if="!isAdminPanel">
            <cognitive-list-item class="cc-ui-navigation__link cc-ui-navigation__link--back">
              <router-link to="/admin">Data Admin</router-link>
            </cognitive-list-item>
          </template>
          <cognitive-list-item v-if="isAdmin" class="cc-ui-navigation__link cc-ui-navigation__link--back">
            <a :href="orgManagementLink">Org Admin</a>
          </cognitive-list-item>
          <cognitive-list-item
            v-if="isAdmin || isDataAnalyst"
            class="cc-ui-navigation__link cc-ui-navigation__link--back">
            <a :href="racManagementLink">RAC Admin</a>
          </cognitive-list-item>
        </template>
        <cognitive-list-item>
          <cognitive-dropdown
            close-on-leave
            overlap
            content-pos="right"
            close-on-event="cc:dropdown:profile:close"
            name="My profile"
            class="cc-ui-navigation__link cc-ui-navigation__link--profile">
            <template #buttonText>
              {{ userInitials }}
            </template>
            <template #blockText>
              <ProfileMenu />
            </template>
          </cognitive-dropdown>
        </cognitive-list-item>
      </cognitive-list>
    </nav>
    <template v-if="toggleTerminal">
      <TerminalWrapper v-show="toggleTerminal" />
    </template>
  </header>
</template>

<script setup lang="ts">
import { defineAsyncComponent, computed, ComputedRef, onMounted, onBeforeUnmount, watch } from 'vue'
import { cognitiveColours, shortcutString } from '@cognitivecredit/ui-utils'
import getWorkBook from '@/spreadsheets/getWorkBook'
import { eventBus } from '@cognitivecredit/ui-utils'
import { menuList, adminMenuList, MenuList, MenuItem } from '@/controls/navigation/menuLists.ts'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { useRoute } from 'vue-router'

import type { UserState } from '@/stores/User/state'
import type { UserGetters } from '@/stores/User/getters'
import { CompanyState } from '@/stores/Company/state'
import { CompanyGetters } from '@/stores/Company/getters'
import { CompanyActions } from '@/stores/Company/actions'

const Logo = defineAsyncComponent(() => import('@/assets/images/cognitive-credit-compressed.svg'))
const ProfileMenu = defineAsyncComponent(() => import('@/controls/navigation/ProfileMenu.vue'))
const NotificationMenu = defineAsyncComponent(() => import('@/controls/navigation/NotificationMenu.vue'))
const HelpDropdown = defineAsyncComponent(() => import('@/controls/navigation/HelpDropdown.vue'))
const TerminalWrapper = defineAsyncComponent(() => import('@/components/terminal/TerminalWrapper.vue'))
const BetaBanner = defineAsyncComponent(() => import('@/controls/navigation/BetaBanner.vue'))
import SubNav from '@/controls/navigation/SubNav.vue'
import { useCompaniesStore } from '@/piniaStores/companiesStore'
import { storeToRefs } from 'pinia'

const { useGetters: useUserGetters } = createNamespacedHelpers<UserState, UserGetters>('User')
const { isAdmin, isDataAnalyst, user, isExperienceAccess, showMarketData, survey } = useUserGetters([
  'isAdmin',
  'isDataAnalyst',
  'user',
  'isExperienceAccess',
  'showMarketData',
  'survey',
])

const { useGetters: useCompanyGetters, useActions: useCompanyActions } = createNamespacedHelpers<
  CompanyState,
  CompanyGetters,
  CompanyActions
>('Company')
const { companyWatcher, terminalNavigationVisible } = useCompanyGetters(['companyWatcher', 'terminalNavigationVisible'])
const { listenCompanyChanges } = useCompanyActions(['listenCompanyChanges'])

const { useGetters: useAppGetters, useActions: useAppActions } = createNamespacedHelpers('App')
const { visibleTerminal } = useAppGetters(['visibleTerminal'])
const { toggleTerminal } = useAppActions(['toggleTerminal'])

const { useGetters: useNotificationGetters } = createNamespacedHelpers('Notification')
const { notifications } = useNotificationGetters(['notifications'])

const { useGetters: useModalGetters, useActions: useModalActions } = createNamespacedHelpers('Modal')
const { modals } = useModalGetters(['modals'])
const { openModal } = useModalActions(['openModal'])

const { useGetters: useComparablesGetters } = createNamespacedHelpers('Comparables')
const { initialising } = useComparablesGetters(['initialising'])
const companiesStore = useCompaniesStore()
const { clientHasAnyDealDocs } = storeToRefs(companiesStore)

const route = useRoute()

const terminalShortcut = ['Alt', 'T']

eventBus.$on('cc:esc:toggle', () => handleTerminal())

onMounted(() => {
  if (user.value) {
    listenCompanyChanges()
  }
})

onBeforeUnmount(() => {
  eventBus.$off('cc:esc:toggle')
})

const isAdminPanel: ComputedRef<boolean> = computed(() => {
  return /\/admin($|.+)/gim.test(route.path)
})

const orgManagementLink: ComputedRef<string> = computed(() => {
  return `${window.location.origin}/organisation-management`
})

const racManagementLink: ComputedRef<string> = computed(() => {
  return `${window.location.origin}/rac-management`
})

const userInitials: ComputedRef<string> = computed(() => {
  if (user.value && user.value.attributes.given_name && user.value.attributes.family_name) {
    return `${user.value.attributes.given_name.charAt(0)}${user.value.attributes.family_name.charAt(0)}`
  }
  return 'AN'
})

const userHasChatAppAccess: ComputedRef<boolean> = computed(() => {
  return user.value?.client?.chatApp ?? false
})

const hasVisibleModals: ComputedRef<boolean> = computed(() => {
  return modals.value.length > 0 && modals.value.findIndex((item) => item.visible) > -1
})

const comparablesLinksDisabled: ComputedRef<boolean> = computed(() => {
  return initialising.value
})

const terminalShortcutString: ComputedRef<string> = computed(() => {
  return shortcutString(terminalShortcut)
})

const trialBannerColours: ComputedRef<{ background: string; text: string }> = computed(() => {
  const { client } = user.value
  if (client.trialDates.trialDaysRemaining && client.trialDates.trialDaysRemaining > 5) {
    return {
      background: cognitiveColours.jungleGreen,
      text: cognitiveColours.blackPearl,
    }
  } else if (client.trialDates.trialDaysRemaining) {
    return {
      background: cognitiveColours.goldenTanoi,
      text: cognitiveColours.blackPearl,
    }
  } else {
    return {
      background: cognitiveColours.brickRed,
      text: cognitiveColours.white,
    }
  }
})

const trialBannerText: ComputedRef<string> = computed(() => {
  return user.value.client.trialDates.trialDaysRemaining
    ? `Your trial will end in ${user.value.client.trialDates.trialDaysRemaining} ${
        user.value.client.trialDates.trialDaysRemaining > 1 ? 'days' : 'day'
      }`
    : 'Your trial has completed'
})

const showTrialBanner: ComputedRef<boolean> = computed(() => {
  return (
    ['trial', 'experience'].includes(user.value.client.status) &&
    typeof user.value.client?.trialDates?.trialDaysRemaining === 'number'
  )
})

const resolveCompsMenu = (compsMenuItem: MenuItem): MenuItem => {
  if (showMarketData.value) {
    const compsSubMenuWithMarketMoversAndNewNames = [
      {
        text: 'Company Comps',
        path: '/comparables/company',
      },
      {
        text: 'Market Comps',
        path: '/comparables/market',
      },
      {
        text: 'Market Movers',
        path: '/comparables/movers',
      },
      {
        text: 'Sector Comps',
        path: '/comparables/sector',
      },
    ]
    return Object.assign({}, compsMenuItem, {
      subMenu: compsSubMenuWithMarketMoversAndNewNames,
    })
  }
  if (!showMarketData.value) {
    return Object.assign({}, compsMenuItem, {
      subMenu: compsMenuItem.subMenu?.filter((subItem) => !subItem.text?.match(/^Market/i)),
    })
  }

  return Object.assign({}, compsMenuItem, {
    subMenu: compsMenuItem.subMenu,
  })
}

const menu: ComputedRef<MenuList> = computed(() => {
  let list = [...(isAdminPanel.value ? adminMenuList : menuList)]
  if (!isAdminPanel.value) {
    if (!userHasChatAppAccess.value) {
      list = list.filter((item) => item.text !== 'Cognitive Credit AI')
    }
    if (!showMarketData.value) {
      // Remove item for users with different special permissions
      const itemToCleanIndex = list.findIndex((item) => item.text === 'Comparables')
      if (itemToCleanIndex > -1) {
        const itemToClean = list[itemToCleanIndex]
        const cleanedItem = Object.assign({}, itemToClean, {
          subMenu: itemToClean.subMenu?.filter((subItem) => subItem.text !== 'Market'),
        })

        list[itemToCleanIndex] = cleanedItem
      }
    }
    const compsMenuIndex = list.findIndex((item) => item.text === 'Comparables')
    if (compsMenuIndex > -1) {
      list[compsMenuIndex] = resolveCompsMenu(list[compsMenuIndex])
    }
    if (comparablesLinksDisabled.value) {
      const itemToDisableIndex = list.findIndex((item) => item.text === 'Comparables')
      if (itemToDisableIndex > -1) {
        const itemToDisable = list[itemToDisableIndex]
        const disabledItem = Object.assign({}, itemToDisable, {
          subMenu: itemToDisable.subMenu?.map((subItem) => ({ ...subItem, disabled: true })),
        })

        list[itemToDisableIndex] = disabledItem
      }
    }
    if (isExperienceAccess.value) {
      const itemToDisableIndex = list.findIndex((item) => item.text === 'Comparables')
      list[itemToDisableIndex].title = `This feature is restricted for your account type.`
      list[itemToDisableIndex].disabled = true
    }
    if (!clientHasAnyDealDocs.value) {
      list = list.filter((item) => item.text !== 'DealConnect')
    }
  } else {
    if (isDataAnalyst.value && !isAdmin.value) {
      list = list.filter((navigationItem) => navigationItem.text === 'Queries')
    }
  }

  return list
})

watch(
  () => user.value,
  (newVal) => {
    if (newVal) {
      listenCompanyChanges()
    }
  },
)

watch(
  () => terminalNavigationVisible.value,
  (newVal) => {
    toggleTerminal(newVal)
  },
)

const handleTerminal = () => {
  const workBook = getWorkBook()
  if (!isSpreadSheetFocused()) {
    if (hasVisibleModals.value) {
      // TODO: understand whats going on here
      toggleTerminal(false)
    } else {
      toggleTerminal(!visibleTerminal.value)

      if (workBook) {
        workBook.focus(!visibleTerminal.value)
      }
    }
  } else {
    if (workBook) {
      workBook.focus(false)
      toggleTerminal(true)
    }
  }
}

const isSpreadSheetFocused = (): boolean => {
  const elem = document.activeElement
  if (!elem) {
    return false
  } else {
    return !!elem.getAttribute('contenteditable')
  }
}

const dropdownClickHandler = (event: Event, item: MenuItem) => {
  const target = event.target as HTMLElement
  if (item.text === 'Comparables' && isExperienceAccess.value) {
    target.blur()
    event.preventDefault()
    event.stopImmediatePropagation()
    openModal({
      name: 'experienceAccessComparables',
    })
  }
}
</script>

<style lang="scss">
// Custom vars
$brand-h: 2rem;
$brand-link-w: 2rem;
$brand-w: 1.8125rem;
$profile-initials-w: 2rem;
$user-button-h: 1.75rem;
$nav-logo-h: 1.5rem;
$notification-tooltip-w: 7.5rem;
$initial-h: 3rem;

.cc-ui-navigation {
  position: relative;
  z-index: 4;
  line-height: $lh-sm;
  height: $header-height;
  contain: layout;

  .cc-ui-ddown {
    height: 20px;
    align-self: center;
  }

  & &__link--profile {
    height: 100%;
    .cc-ui-button {
      height: 2rem;
      &:before {
        border-bottom: none;
        outline: none;
      }
    }
  }

  &__terminal {
    button {
      @extend %decoration;
      background: transparent;
      border: none;
      color: $white;
      height: 1.25rem;

      &:hover,
      &:focus {
        &:before {
          transition:
            width 0.25s ease-in,
            left 0.25s ease-in;
          left: 5%;
          width: 90%;
        }
      }

      &:focus-visible {
        outline: revert;
        box-shadow: none;
        &:after {
          outline: 5px auto -webkit-focus-ring-color;
        }
      }
    }
  }

  @at-root .is-modal-visible::not('.is-draggable-modal-visible') & {
    @media #{$mq-retina} {
      &::before {
        content: '';
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: rgba($black-pearl, 0.85);
        pointer-events: none;
      }
    }
  }

  @at-root .terminal-open & {
    z-index: 5;
  }

  &__banner-link {
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: $sp-sm $sp-md;
    margin-top: $sp-xs;
    margin-right: $sp-xs;
    max-height: 2rem;
    &:hover {
      filter: brightness(105%);
    }

    .cc-ui-icon {
      height: 17px;
      margin-left: $sp-sm;
    }
  }

  &__main {
    background: $black-pearl;
    padding: $sp-xs $sp-sm;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-content: stretch;
    align-items: center;

    @at-root .cc-is-offline & {
      background: $black-pearl--offline;
    }

    > ul {
      display: flex;
      align-self: stretch;

      > li {
        height: 100%;
        display: inline-flex;
        align-items: center;

        > .cc-ui-button {
          color: $white;
          border: 0;
          padding: 0 $sp-xs 0 $sp-sm;
          font-size: $fs-xs;
        }

        .cc-ui-navigation__survey-link {
          padding: 0.5rem 0;
        }
      }
    }
  }

  &__link {
    position: relative;
    flex: 0 1 auto;
    height: 100%;

    > a {
      @extend %decoration;
      display: flex;
      align-self: center;
      padding: $sp-xs $sp-sm;
      color: $white;
      font-size: $fs-xs;

      &:hover,
      &:focus,
      &.is-active {
        color: $white;

        &:before {
          transition:
            width 0.25s ease-in,
            left 0.25s ease-in;
          left: 5%;
          width: 90%;
        }
      }

      &.is-active {
        &:after {
          display: block;
          transition: 0.15s opacity 0.25s ease-in;
        }
      }

      &:focus-visible {
        outline: revert;
        box-shadow: none;
      }
    }

    &.is-active,
    &:hover,
    &:focus {
      .cc-ui-button {
        &:before {
          transition:
            width 0.25s ease-in,
            left 0.25s ease-in;
          left: 5%;
          width: 90%;
        }
      }
    }
    &.is-active {
      .cc-ui-button {
        &:after {
          display: block;
          transition: 0.15s opacity 0.25s ease-in;
        }
      }
    }
    &::before {
      white-space: pre-wrap;
      width: 10rem;
    }

    .cc-ui-button {
      @extend %decoration;
      color: $baby-powder;
      border: 0;
      background: transparent;
      padding: $sp-xs $sp-sm;
      font-size: $fs-xs;

      @at-root .cc-ui-navigation__tools & {
        padding: $sp-xs 0;
      }

      &:after {
        display: block;
        transition: 0.15s opacity 0.25s ease-in;
      }
      &:hover {
        .iconNotification {
          &::before {
            background: $porcelain;
          }
        }
      }

      .iconNotification {
        &::before {
          background: $white;
        }
      }

      &:focus {
        &:after {
          outline: 5px auto -webkit-focus-ring-color;
        }
      }

      &:hover,
      &:focus,
      &:focus-visible {
        outline: revert;
        box-shadow: none;
        &:after {
          transition:
            width 0.25s ease-in,
            left 0.25s ease-in;
        }
      }
    }

    .cc-ui-ddown__content {
      top: calc(100% - #{$sp-xs});
    }

    &--has-notifications {
      &:before {
        content: '';
        position: absolute;
        right: 0;
        top: $sp-sm;
        width: $sp-sm;
        height: $sp-sm;
        border-radius: 50%;
        background: $golden-tanoi;
      }
    }

    &--no-notifications {
      .iconNotification {
        &::before {
          opacity: 0.5;
        }
      }
    }

    &--profile {
      position: relative;
      padding: $sp-xs 0;
      margin-left: $sp-sm;

      > .cc-ui-button {
        padding: 0 0;
        background: $jungle-green;
        color: $black-pearl;
        border-radius: 50%;
        border: 0;
        width: $profile-initials-w;

        @at-root .cc-is-offline & {
          background: $jungle-green--offline;
        }
      }

      .cc-menu-dropdown {
        &:after {
          right: -$sp-xs;
        }
      }
    }
    &--disabled {
      cursor: not-allowed;
      color: $gray-chateau;
      .cc-ui-button {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  &__reconnect {
    &.cc-ui-button {
      &:focus,
      &:hover {
        .iconNotificationOffline {
          &::before {
            background: $porcelain;
          }
        }
      }

      .iconNotificationOffline {
        &::before {
          background: $baby-powder;
        }
      }
    }

    &[data-title-right] {
      &:before {
        min-width: $notification-tooltip-w;
      }
    }
  }

  &__controls {
    flex: 0 1 auto;
    text-align: left;
  }

  &__brand {
    display: flex;
    width: $brand-link-w;
    height: $brand-h;
    align-items: center;
  }

  &__logo {
    height: $nav-logo-h;
    width: $brand-w;
    padding: 0;
    cursor: pointer;
    display: inline-block;
    vertical-align: middle;
    fill: $baby-powder;
  }
  &__bell {
    @at-root .cc-ui-navigation__tools & .cc-ui-button {
      padding: 0;
    }
  }
}

%decoration {
  position: relative;

  &:after {
    content: '';
    width: $sp-sm;
    height: $sp-sm;
    background: $black-pearl;
    border-radius: 50%;
    border: 1px solid $white;
    position: absolute;
    bottom: -$sp-sm;
    left: 45%;
    z-index: 0;
    display: none;
  }

  &:before {
    content: '';
    position: absolute;
    width: 0;
    bottom: -$sp-xs;
    left: 50%;
    margin: auto;
    border-bottom: 1px solid $white;
    z-index: 0;
  }
}
</style>
